import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import i18n from "../locales/i18n";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * 格式化货币金额（USDT）
 * @param amount - 金额数值
 * @param decimals - 小数位数，默认 3（与服务器 MONEY_DECIMALS 一致，避免小额收益被舍入成 0）
 * @returns 格式化后的金额字符串，如 "$12,580.500"
 */
export function formatCurrency(amount: number, decimals: number = 3): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
}

/**
 * 格式化大数字（K/M/B 单位）
 * @param num - 数值
 * @returns 格式化后的字符串，如 "1.2M"
 */
export function formatLargeNumber(num: number): string {
  if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(1)}B`;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toString();
}

/**
 * 格式化百分比
 * @param value - 百分比数值（0-100）
 * @param decimals - 小数位数，默认 1
 * @returns 格式化后的百分比字符串，如 "68.5%"
 */
export function formatPercent(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * 格式化盈亏数值，带正负号
 * @param value - 盈亏数值
 * @returns 格式化后的字符串，如 "+$1,234.56"
 */
export function formatPnl(value: number): string {
  const prefix = value >= 0 ? '+' : '';
  return `${prefix}${formatCurrency(value)}`;
}

/**
 * 格式化时间戳为相对时间
 * @param dateStr - 日期字符串
 * @returns 相对时间字符串，如 "3天后"
 */
export function formatRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '--';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '--';
  const now = new Date();
  const diff = date.getTime() - now.getTime();
  const absDiff = Math.abs(diff);
  const minutes = Math.floor(absDiff / 60000);
  const hours = Math.floor(absDiff / 3600000);
  const days = Math.floor(absDiff / 86400000);
  const isFuture = diff > 0;
  // 文案走 i18n（原来写死英文，中文界面也会显示 "in 3d"）
  if (isFuture) {
    if (days > 0) return i18n.t('common.daysLater', { d: days });
    if (hours > 0) return i18n.t('common.hoursLater', { h: hours });
    if (minutes > 0) return i18n.t('common.minutesLater', { m: minutes });
    return i18n.t('common.justNow');
  } else {
    if (days > 0) return i18n.t('common.daysAgo', { d: days });
    if (hours > 0) return i18n.t('common.hoursAgo', { h: hours });
    if (minutes > 0) return i18n.t('common.minutesAgo', { m: minutes });
    return i18n.t('common.justNow');
  }
}

/**
 * 格式化日期为标准格式
 * @param dateStr - 日期字符串
 * @returns 格式化后的日期字符串
 */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '--';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '--';
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).format(date);
}

/**
 * 根据数值正负返回颜色类名
 * @param value - 数值
 * @returns CSS 颜色类名
 */
export function getPnlColorClass(value: number): string {
  return value >= 0 ? 'text-up' : 'text-down';
}

/**
 * 截断地址字符串（用于钱包地址显示）
 * @param address - 完整地址
 * @param start - 保留开头字符数，默认 6
 * @param end - 保留结尾字符数，默认 4
 * @returns 截断后的地址
 */
export function truncateAddress(address: string, start = 6, end = 4): string {
  if (address.length <= start + end) return address;
  return `${address.slice(0, start)}...${address.slice(-end)}`;
}

/**
 * 计算预测市场的潜在回报
 * @param amount - 下注金额
 * @param price - 当前价格（0-1）
 * @returns 潜在总回报金额
 */
export function calcPotentialReturn(amount: number, price: number): number {
  if (price <= 0 || price >= 1) return 0;
  return amount / price;
}

/**
 * 获取市场分类的显示图标
 * @param category - 市场分类
 * @returns 对应的 emoji 图标
 */
const CATEGORY_ICON_URLS: Record<string, string> = {
  // All: 金色网格图标（本地）
  all:       '/icons/cat-all.webp',
  // High-Freq: 高频市场图标（本地）
  high_freq: '/icons/cat-high-freq.webp',
  // Crypto: BTC 图标（本地）
  crypto:    '/icons/cat-crypto.png',
  // Commodity: PAXG 黄金 token 图标（本地）
  commodity: '/icons/cat-commodity.png',
  // Forex: 美元/欧元兑换图标（本地）
  forex:     '/icons/cat-forex.webp',
  // Stock: 蓝色K线+楼宇图标（本地）
  stock:     '/icons/cat-stock.webp',
  // Index: 绿色上升趋势线图标（本地）
  index:     '/icons/cat-index.webp',
  // Event: 紫色日历+问号图标（本地）
  event:     '/icons/cat-event.webp',
};

export function getCategoryIconUrl(category: string): string {
  return CATEGORY_ICON_URLS[category] || CATEGORY_ICON_URLS['all'];
}

/**
 * 按标的（资产符号）显示的图标，优先于分类图标。
 * 例：BTCUSDT→比特币、ETHUSDT→以太坊、XAUUSDT→黄金、XAGUSDT→白银。
 * 后端把贵金属/外汇标的也归到 crypto 分类，单靠分类图标会全部显示成 BTC，
 * 因此这里按 symbol 取专属图标，无匹配时再回退到分类图标。
 */
const ASSET_ICON_URLS: Record<string, string> = {
  BTC: '/icons/assets/btc.svg',
  ETH: '/icons/assets/eth.svg',
  XAU: '/icons/assets/xau.svg',
  XAG: '/icons/assets/xag.svg',
};

/** 从交易对符号中提取基础资产 key（去掉计价货币后缀与分隔符）。 */
function normalizeAssetKey(symbol?: string): string {
  if (!symbol) return '';
  // 取分隔符（/ - _ 空格）前的部分，如 "XAU/USD" → "XAU"
  let s = symbol.toUpperCase().trim().split(/[/\-_\s]/)[0];
  // 去掉计价货币后缀，如 "ETHUSDT" → "ETH"
  s = s.replace(/(USDT|USDC|BUSD|USD)$/, '');
  return s;
}

/**
 * 获取市场图标 URL：优先按标的，无对应标的图标时回退到分类图标。
 * @param symbol - 资产符号（如 ETHUSDT、XAU/USD）
 * @param category - 市场分类（回退用）
 */
export function getAssetIconUrl(symbol: string | undefined, category: string): string {
  const key = normalizeAssetKey(symbol);
  if (key && ASSET_ICON_URLS[key]) return ASSET_ICON_URLS[key];
  return getCategoryIconUrl(category);
}

export function getCategoryIcon(category: string): string {
  const url = getCategoryIconUrl(category);
  return `<img src="${url}" alt="${category}" style="width:1.2em;height:1.2em;object-fit:contain;display:inline-block;vertical-align:middle;" />`;
}

/**
 * 把剩余秒数格式化为「X天X小时X分X秒」（多语言）。
 * 省略前导为 0 的高位单位（天/小时），始终保留分与秒；不足 0 视为 0 秒。
 * 单位文案取自 i18n（market.card.unit*）：zh→天/小时/分/秒，en→d/h/m/s。
 * 例：zh 90061→"1天1小时1分1秒"；en 305→"5m 5s"。
 */
export function formatCountdownDHMS(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  const u = {
    d: i18n.t('market.card.unitDay'),
    h: i18n.t('market.card.unitHour'),
    m: i18n.t('market.card.unitMin'),
    s: i18n.t('market.card.unitSec'),
  };
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}${u.d}`);
  if (days > 0 || hours > 0) parts.push(`${hours}${u.h}`);
  if (days > 0 || hours > 0 || mins > 0) parts.push(`${mins}${u.m}`);
  parts.push(`${secs}${u.s}`);
  // 中文不加空格，其它语言用空格分隔以便阅读
  const sep = (i18n.language || '').startsWith('zh') ? '' : ' ';
  return parts.join(sep);
}

/**
 * 复制文本到剪贴板
 * @param text - 要复制的文本
 * @returns 是否复制成功
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
